/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Typography,
  Button,
  Checkbox,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from '../common/iconify.component';
import Sortable from './sortable.component';
import NoData from './no-data.component';
import Error from './error.component';
import Loading from './loading.component';
import CellValue from './cell-value.component';
import { CONNECTED_SOURCE_FOLDER_SYNC_STATUS } from '../../config/const.config';

const ListContentSourcesData = ({
  columns,
  rows,
  page,
  rowsPerPage,
  totalRows,
  loading,
  actions,
  error,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onAction,
  showCheckbox,
  selectedRows,
  setSelectedRows,
  selectAllPages,
  setSelectAllPages,
}) => {
  const colSpan = columns.length + (actions.length ? 1 : 0);

  const [anchorActionEl, setAnchorActionEl] = useState(null);
  const [openActions, setOpenActions] = useState({
    show: false,
    data: null,
  });

  const handleActionOpen = (e, selectedRow) => {
    setAnchorActionEl(e.currentTarget);
    setOpenActions({
      ...openActions,
      show: true,
      data: selectedRow,
    });
  };
  const handleActionClose = () => {
    setAnchorActionEl(null);
    setOpenActions({
      ...openActions,
      show: false,
      data: null,
    });
  };
  const handleActionSelect = (selectedAction) => {
    onAction(selectedAction, openActions.data);
    handleActionClose();
  };
  const handleSingleActionSelect = (data) => {
    onAction(actions[0].action, data);
    handleActionClose();
  };

  const filteredRows = rows.filter(
    (row) =>
      row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.NEW.value ||
      row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.ERROR.value ||
      row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_CSV.value ||
      row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_ASSETS.value
  );

  const isPageSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedRows.includes(row.id));

  const handleRowSelection = (id) => {
    const finalSelectedRows = selectedRows.includes(id)
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];

    setSelectedRows(finalSelectedRows);
    if (finalSelectedRows.length < rowsPerPage) {
      setSelectAllPages(false);
    }
  };

  const handlePageSelection = () => {
    const pageRowIds = rows
      .filter(
        (row) =>
          row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.NEW.value ||
          row.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.ERROR.value ||
          row.status ===
            CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_CSV.value ||
          row.status ===
            CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_ASSETS.value
      )
      .map((row) => row.id);
    if (isPageSelected) {
      setSelectedRows((prevSelected) =>
        prevSelected.filter((id) => !pageRowIds.includes(id))
      );
    } else {
      setSelectedRows((prevSelected) => [
        ...new Set([...prevSelected, ...pageRowIds]),
      ]);
    }
  };

  useEffect(() => {
    if (showCheckbox) {
      if (selectAllPages) {
        setSelectedRows(rows.map((row) => row.id));
      }
    }
  }, [selectAllPages, rows]);

  return (
    <>
      <Grid container spacing={1} display="flex" alignItems="center">
        <Grid item xs={12} sm={4} md={4} />

        {totalRows > 0 && (
          <Grid item xs={12} sm={8} md={8}>
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(e, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                onRowsPerPageChange(Number(e.target.value));
              }}
            />
          </Grid>
        )}
      </Grid>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {showCheckbox &&
                !loading &&
                !error &&
                filteredRows.length !== 0 && (
                  <TableCell align="center" width="2%">
                    <Checkbox
                      checked={isPageSelected}
                      onChange={handlePageSelection}
                    />
                  </TableCell>
                )}

              {actions.length > 0 && (
                <TableCell align="center" width="5%">
                  Action
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell
                  key={`col-${c.id}`}
                  align={c.align}
                  width={c.width}
                  sx={{
                    cursor: c.sortable ? 'pointer' : 'inherit',
                    padding: '16px',
                    fontSize: '0.875rem !important',
                  }}
                  onClick={() => {
                    if (c.sortable) {
                      if (c.dataKey === sortBy) {
                        if (sortOrder === '') {
                          onSortChange(c.dataKey, 'asc');
                        } else if (sortOrder === 'asc') {
                          onSortChange(c.dataKey, 'desc');
                        } else if (sortOrder === 'desc') {
                          onSortChange('', '');
                        }
                      } else {
                        onSortChange(c.dataKey, 'asc');
                      }
                    }
                  }}
                >
                  <Stack direction="row" justifyContent={c.align}>
                    {c.sortable && (
                      <Sortable
                        dataKey={c.dataKey}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                      />
                    )}
                    {c.label}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && !error && (
              <>
                {rows.map((r, index) => (
                  <TableRow key={`row-${r[columns[0].dataKey]}-${index}`}>
                    {showCheckbox &&
                    !loading &&
                    !error &&
                    (r.status ===
                      CONNECTED_SOURCE_FOLDER_SYNC_STATUS.NEW.value ||
                      r.status ===
                        CONNECTED_SOURCE_FOLDER_SYNC_STATUS.ERROR.value ||
                      r.status ===
                        CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_CSV.value ||
                      r.status ===
                        CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_ASSETS
                          .value) ? (
                      <TableCell align="center" width="2%">
                        <Checkbox
                          checked={selectedRows.includes(r.id)}
                          onChange={() => {
                            handleRowSelection(r.id);
                          }}
                        />
                      </TableCell>
                    ) : filteredRows.length !== 0 ? (
                      <TableCell align="center" width="2%" />
                    ) : null}

                    {actions.length > 1 && (
                      <TableCell align="center" width="5%">
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={(e) => handleActionOpen(e, r)}
                        >
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </TableCell>
                    )}

                    {actions.length === 1 && (
                      <TableCell align="right" width="5%">
                        <Button
                          size="small"
                          color="info"
                          variant="contained"
                          onClick={() => handleSingleActionSelect(r)}
                        >
                          {actions[0].label}
                        </Button>
                      </TableCell>
                    )}
                    {columns.map((c) => (
                      <TableCell
                        key={`data-col-${c.id}-${r[c.dataKey]}`}
                        align={c.align}
                        width={c.width}
                        sx={{
                          padding: '16px',
                          fontSize: '0.875rem !important',
                        }}
                        variant="string"
                      >
                        <CellValue
                          type={c.type}
                          dataKey={c.dataKey}
                          value={r[c.dataKey]}
                        />

                        {c.secondaryFields && (
                          <>
                            {c.secondaryFields.map((sf, sfIdx) => (
                              <React.Fragment
                                key={`sf-data-col-${c.id}-${sfIdx}`}
                              >
                                <br />
                                <CellValue
                                  type={sf.type}
                                  dataKey={sf.dataKey}
                                  value={r[sf.dataKey]}
                                  color="secondary"
                                />
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}

            {!loading && !error && totalRows === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <NoData />
                </TableCell>
              </TableRow>
            )}

            {!loading && error && totalRows === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <Error />
                </TableCell>
              </TableRow>
            )}

            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <Loading />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalRows > 0 && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            onRowsPerPageChange(Number(e.target.value));
          }}
        />
      )}

      {openActions.show && (
        <Menu
          id="actions-menu"
          anchorEl={anchorActionEl}
          open
          onClose={() => handleActionClose()}
          size="small"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {actions.map((a, index) => {
            let render = true;
            if (a.conditional) {
              render = a.conditional(openActions.data);
            }

            if (render) {
              return (
                <MenuItem
                  key={`mia-${a.action}-${index}`}
                  onClick={() => handleActionSelect(a.action)}
                  disabled={
                    a.conditionalDisabled &&
                    a.conditionalDisabled(openActions.data)
                  }
                >
                  <Typography variant="body2">{a.label}</Typography>
                </MenuItem>
              );
            }

            return null;
          })}
        </Menu>
      )}
    </>
  );
};

ListContentSourcesData.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  error: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  showCheckbox: PropTypes.bool,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func,
  selectAllPages: PropTypes.bool,
  setSelectAllPages: PropTypes.func,
};
ListContentSourcesData.defaultProps = {
  onAction: () => {},
  showCheckbox: false,
  selectedRows: [],
  setSelectedRows: () => {},
  selectAllPages: false,
  setSelectAllPages: () => {},
};

export default ListContentSourcesData;
